// frontend/src/components/ActivityFormFields.js
import React, {
  useEffect,
  useMemo,
  useState,
  useCallback,
  useRef,
} from "react";
import { Check, X } from "phosphor-react";
import { api } from "../api";

import MentionTextarea from "./MentionTextarea";
import LocationAutoComplete from "./LocationAutoComplete";
import MediaCropperModal from "./MediaCropperModal";

/** -------- Canvas helper: crop to a Blob -------- */
async function getCroppedImage(srcUrl, cropAreaPixels, mime = "image/png", quality = 0.92) {
  const img = await new Promise((res, rej) => {
    const i = new Image();
    i.onload = () => res(i);
    i.onerror = rej;
    i.src = srcUrl; // object URL or data URL both work
  });
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.floor(cropAreaPixels.width));
  canvas.height = Math.max(1, Math.floor(cropAreaPixels.height));
  const ctx = canvas.getContext("2d");
  ctx.imageSmoothingEnabled = true;
  ctx.imageSmoothingQuality = "high";
  ctx.drawImage(
    img,
    cropAreaPixels.x,
    cropAreaPixels.y,
    cropAreaPixels.width,
    cropAreaPixels.height,
    0,
    0,
    canvas.width,
    canvas.height
  );
  const blob = await new Promise((res) => canvas.toBlob(res, mime, quality));
  return blob;
}

/** -------- Duration helpers -------- */
function parseDuration(str = "") {
  const h = /(\d+)\s*h/i.exec(str)?.[1];
  const m = /(\d+)\s*m/i.exec(str)?.[1];
  return {
    hours: Number.isFinite(Number(h)) ? Number(h) : 0,
    minutes: Number.isFinite(Number(m)) ? Number(m) : 0,
  };
}
function formatDuration(hours, minutes) {
  const h = Number(hours) || 0;
  const m = Number(minutes) || 0;
  if (h && m) return `${h}h ${m}m`;
  if (h) return `${h}h`;
  if (m) return `${m}m`;
  return "";
}

/** -------- Utility -------- */
const toNumberArray = (arr) =>
  (Array.isArray(arr) ? arr : [])
    .map((v) => Number(v))
    .filter((n) => Number.isFinite(n));

const MAX_MEDIA = 5;

const normalizeTag = (value = "") =>
  String(value)
    .trim()
    .replace(/^#+/, "")
    .replace(/\s+/g, " ");

const parseTags = (value) => {
  if (Array.isArray(value)) {
    return value
      .map(normalizeTag)
      .filter(Boolean);
  }

  return String(value || "")
    .split(",")
    .map(normalizeTag)
    .filter(Boolean);
};

function TagInput({ value, onChange }) {
  const tags = useMemo(() => parseTags(value), [value]);

  const [draft, setDraft] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [loading, setLoading] = useState(false);

  const wrapperRef = useRef(null);
  const inputRef = useRef(null);

  const emitTags = useCallback(
    (nextTags) => {
      const unique = [];
      const seen = new Set();

      nextTags.forEach((rawTag) => {
        const tag = normalizeTag(rawTag);
        const key = tag.toLowerCase();

        if (!tag || seen.has(key)) return;

        seen.add(key);
        unique.push(tag);
      });

      // Keep the existing form shape for compatibility.
      onChange(unique.join(", "));
    },
    [onChange]
  );

  useEffect(() => {
    const handleOutsideClick = (event) => {
      if (
        wrapperRef.current &&
        !wrapperRef.current.contains(event.target)
      ) {
        setOpen(false);
      }
    };

    document.addEventListener("mousedown", handleOutsideClick);

    return () => {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, []);

  useEffect(() => {
    const query = normalizeTag(draft);

    if (!query) {
      setSuggestions([]);
      setOpen(false);
      setLoading(false);
      return;
    }

    const controller = new AbortController();

    const timer = window.setTimeout(async () => {
      setLoading(true);

      try {
        const token = localStorage.getItem("token");

        const response = await api.get("/activities/tags/search", {
          params: {
            q: query,
            limit: 8,
          },
          headers: token
            ? { Authorization: `Bearer ${token}` }
            : undefined,
          signal: controller.signal,
        });

        const existingKeys = new Set(
          tags.map((tag) => tag.toLowerCase())
        );

        const nextSuggestions = Array.isArray(response.data?.tags)
          ? response.data.tags
              .map(normalizeTag)
              .filter(Boolean)
              .filter(
                (tag) => !existingKeys.has(tag.toLowerCase())
              )
          : [];

        setSuggestions(nextSuggestions);
        setActiveIndex(0);
        setOpen(true);
      } catch (error) {
        if (
          error?.name !== "CanceledError" &&
          error?.code !== "ERR_CANCELED"
        ) {
          setSuggestions([]);
        }
      } finally {
        setLoading(false);
      }
    }, 180);

    return () => {
      window.clearTimeout(timer);
      controller.abort();
    };
  }, [draft, tags]);

  const commitTag = useCallback(
    (rawTag) => {
      const tag = normalizeTag(rawTag);

      if (!tag) return;

      const exists = tags.some(
        (existingTag) =>
          existingTag.toLowerCase() === tag.toLowerCase()
      );

      if (!exists) {
        emitTags([...tags, tag]);
      }

      setDraft("");
      setSuggestions([]);
      setOpen(false);
      setActiveIndex(0);

      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    },
    [emitTags, tags]
  );

  const removeTag = useCallback(
    (indexToRemove) => {
      emitTags(
        tags.filter((_, index) => index !== indexToRemove)
      );

      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    },
    [emitTags, tags]
  );

  const handleKeyDown = (event) => {
    if (
      event.key === "ArrowDown" &&
      open &&
      suggestions.length > 0
    ) {
      event.preventDefault();

      setActiveIndex((index) =>
        index >= suggestions.length - 1 ? 0 : index + 1
      );

      return;
    }

    if (
      event.key === "ArrowUp" &&
      open &&
      suggestions.length > 0
    ) {
      event.preventDefault();

      setActiveIndex((index) =>
        index <= 0 ? suggestions.length - 1 : index - 1
      );

      return;
    }

    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();

      if (open && suggestions[activeIndex]) {
        commitTag(suggestions[activeIndex]);
      } else {
        commitTag(draft);
      }

      return;
    }

    if (event.key === "Escape") {
      setOpen(false);
      return;
    }

    if (
      event.key === "Backspace" &&
      !draft &&
      tags.length > 0
    ) {
      removeTag(tags.length - 1);
    }
  };

  const normalizedDraft = normalizeTag(draft);

  const exactSuggestionExists = suggestions.some(
    (suggestion) =>
      suggestion.toLowerCase() === normalizedDraft.toLowerCase()
  );

  return (
    <div ref={wrapperRef} className="relative">
      <div
        className="
          flex min-h-[34px] w-full flex-wrap items-center gap-1
          !min-h-0 !min-w-0 px-2.5 py-1.5
        "
        onClick={() => inputRef.current?.focus()}
      >
        {tags.map((tag, index) => (
          <span
            key={`${tag}-${index}`}
            className="
              inline-flex items-center gap-1 rounded-full
              bg-blue-100 !min-h-0 !min-w-0 px-2 py-1 text-sm text-blue-700
            "
          >
            #{tag}

            <button
              type="button"
              onClick={(event) => {
                event.stopPropagation();
                removeTag(index);
              }}
              className="
                inline-flex !min-h-0 !min-w-0 items-center
                justify-center rounded-full !min-h-0 !min-w-0
                hover:bg-blue-200
              "
              aria-label={`Remove #${tag}`}
            >
              <X size={10} weight="bold" />
            </button>
          </span>
        ))}

        <div
          className="
            inline-flex max-w-full items-center rounded-2xl border bg-white
            !min-h-0 !min-w-0 pl-2 py-1
          "
        >
          <span className="shrink-0 text-sm text-gray-500">#</span>

          <input
            ref={inputRef}
            type="text"
            value={draft}
            onChange={(event) => {
              setDraft(event.target.value.replace(/^#+/, ""));
              setOpen(true);
            }}
            onFocus={() => {
              if (normalizedDraft) setOpen(true);
            }}
            onKeyDown={handleKeyDown}
            placeholder={tags.length === 0 ? "Add a tag" : "Add another"}
            style={{
              width: `${Math.max(
                6,
                (draft || (tags.length === 0 ? "Add a tag" : "Add another")).length - 1.3
              )}ch`,
            }}
            className="
              max-w-full border-0 bg-transparent
              !min-h-0 !min-w-0 pl-0.5 py-0 text-sm outline-none
              focus:ring-0
            "
            autoComplete="off"
          />
        </div>
      </div>

      {open && normalizedDraft && (
        <div
          className="
            absolute left-0 right-0 z-30 mt-1
            max-h-52 overflow-y-auto rounded-2xl
            border bg-white p-1 shadow-lg
          "
        >
          {loading && suggestions.length === 0 ? (
            <p className="!min-h-0 !min-w-0 px-3 py-1 text-sm text-gray-500">
              Searching tags…
            </p>
          ) : (
            <>
              {suggestions.map((suggestion, index) => (
                <div key={suggestion} className="px-1">
                  <button
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    onClick={() => commitTag(suggestion)}
                    className={[
                      "inline-flex !min-h-0 !min-w-0 items-center",
                      "rounded-2xl px-2 py-1 text-left text-sm",
                      index === activeIndex
                        ? "bg-blue-100 text-blue-700"
                        : "hover:bg-gray-50",
                    ].join(" ")}
                  >
                    #{suggestion}
                  </button>
                </div>
              ))}

              {!exactSuggestionExists && (
                <div className="px-1">
                  <button
                    type="button"
                    onMouseDown={(event) => {
                      event.preventDefault();
                    }}
                    onClick={() => commitTag(normalizedDraft)}
                    className="
                      inline-flex !min-h-0 !min-w-0 items-center
                      rounded-xl px-2 py-1 text-left text-sm text-gray-600
                      hover:bg-gray-50
                    "
                  >
                    Create
                    <span className="ml-1 font-medium text-blue-600">
                      #{normalizedDraft}
                    </span>
                  </button>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Shared form fields for Activity create/edit.
 * Now supports multiple media: form.media_files (array of File) + legacy form.media_file (first item).
 */
export default function ActivityFormFields({
  form,
  setForm,
  circleId = null,
  joinedCircles = [],
  showCircles = true,
  showTimestamp = true,
  previewUrl,           // legacy single preview (kept for compatibility)
  setPreviewUrl,        // legacy setter (we still set it to the first item)
  currentMediaUrls = [],
}) {
  /** ------- Duration state (derived from form.duration) ------- */
  const initialDur = useMemo(() => parseDuration(form.duration), [form.duration]);
  const [durHours, setDurHours] = useState(initialDur.hours);
  const [durMinutes, setDurMinutes] = useState(initialDur.minutes);

  useEffect(() => {
    setForm((s) => ({ ...s, duration: formatDuration(durHours, durMinutes) }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [durHours, durMinutes]);

  /** ------- Multi-media state ------- */
  // We store the canonical files on the form: media_files (array) and media_file (first)
  const mediaFiles = Array.isArray(form.media_files) ? form.media_files : [];
  const existingMediaUrls = Array.isArray(form.existing_media_urls)
    ? form.existing_media_urls
    : [];

  const totalMediaCount = existingMediaUrls.length + mediaFiles.length;
  const [previewUrls, setPreviewUrls] = useState([]); // [{url, type, idx}]
  const isPrivate = form.visibility === "private";
  const urlRefs = useRef([]); // for cleanup of object URLs

  // Keep preview URLs in sync with mediaFiles
  useEffect(() => {
    // revoke previous
    urlRefs.current.forEach((u) => URL.revokeObjectURL(u));
    urlRefs.current = [];

    const next = mediaFiles.map((f, idx) => {
      const url = URL.createObjectURL(f);
      urlRefs.current.push(url);
      return { url, type: f.type || "", idx };
    });
    setPreviewUrls(next);

    // keep legacy single-preview behavior for first item
    if (setPreviewUrl) {
      const first = next[0]?.url || null;
      setPreviewUrl(first);
    }
    // also keep legacy media_file (first item) updated for backward compatibility
    if (mediaFiles.length > 0) {
      setForm((s) => ({ ...s, media_file: mediaFiles[0] }));
    } else {
      setForm((s) => ({ ...s, media_file: null }));
    }

    return () => {
      // cleanup on unmount
      urlRefs.current.forEach((u) => URL.revokeObjectURL(u));
      urlRefs.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mediaFiles]);

  /** ------- Cropper state (per-image) ------- */
  const [cropSrc, setCropSrc] = useState(null);      // object URL for the image being cropped
  const [showCropper, setShowCropper] = useState(false);
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [cropPixels, setCropPixels] = useState(null);
  const [aspect, setAspect] = useState(4 / 5);       // default aspect for activities
  const [cropTargetIndex, setCropTargetIndex] = useState(null); // which index in mediaFiles we’re cropping

  const onCropComplete = useCallback((_area, areaPixels) => {
    setCropPixels(areaPixels);
  }, []);

  /** ------- Crop queue for newly-selected files ------- */
  const [pendingCrops, setPendingCrops] = useState([]); // array of {file, mime, name}

  const startCropForFile = useCallback((file, replaceIndex = null) => {
    try {
      const url = URL.createObjectURL(file);
      setCropSrc(url);
      setZoom(1);
      setCrop({ x: 0, y: 0 });
      setCropPixels(null);
      setShowCropper(true);
      setCropTargetIndex(replaceIndex); // if null, we'll append; otherwise replace at index
    } catch {
      // if something goes wrong, just append raw
      setForm((s) => {
        const cur = Array.isArray(s.media_files) ? [...s.media_files] : [];
        const retainedCount = Array.isArray(s.existing_media_urls)
          ? s.existing_media_urls.length
          : 0;

        const room = Math.max(
          0,
          MAX_MEDIA - retainedCount - cur.length
        );
        if (room <= 0) return s;
        cur.push(file);
        return { ...s, media_files: cur };
      });
    }
  }, [setForm]);

  const applyCropped = useCallback(async () => {
    if (!cropSrc || !cropPixels) return;
    try {
      const blob = await getCroppedImage(cropSrc, cropPixels, "image/png", 0.92);
      const file = new File([blob], "image.png", { type: "image/png" });

      setForm((s) => {
        const cur = Array.isArray(s.media_files) ? [...s.media_files] : [];
        if (cropTargetIndex != null && cur[cropTargetIndex]) {
          cur[cropTargetIndex] = file;
        } else {
          const retainedCount = Array.isArray(s.existing_media_urls)
            ? s.existing_media_urls.length
            : 0;

          if (retainedCount + cur.length >= MAX_MEDIA) return s;

          cur.push(file);
        }
        return { ...s, media_files: cur };
      });
    } finally {
      URL.revokeObjectURL(cropSrc);
      setCropSrc(null);
      setShowCropper(false);
      setCropTargetIndex(null);
    }

    // If we’re processing a queue of new selections, continue
    setPendingCrops((q) => {
      const next = [...q];
      next.shift();
      if (next.length > 0) {
        // crop next file (append)
        startCropForFile(next[0].file, null);
      }
      return next;
    });
  }, [cropSrc, cropPixels, cropTargetIndex, setForm, startCropForFile]);

  const cancelCrop = useCallback(() => {
    if (cropSrc) URL.revokeObjectURL(cropSrc);
    setCropSrc(null);
    setShowCropper(false);
    setCropTargetIndex(null);

    // If we’re in a new-files queue, skip this one and move on
    setPendingCrops((q) => {
      const next = [...q];
      next.shift();
      if (next.length > 0) {
        startCropForFile(next[0].file, null);
      }
      return next;
    });
  }, [cropSrc, startCropForFile]);

  /** ------- Generic change handler (now multi-file aware) ------- */
  const handleChange = (e) => {
    const { name, value } = e.target;

    if (name === "circle_ids") {
      const selectedIds = Array.from(e.target.selectedOptions)
        .map((o) => Number(o.value))
        .filter((n) => Number.isFinite(n));
      setForm((s) => ({ ...s, circle_ids: selectedIds }));
      return;
    }

    if (name === "duration") {
      const { hours, minutes } = parseDuration(value);
      setDurHours(hours);
      setDurMinutes(minutes);
      setForm((s) => ({ ...s, duration: formatDuration(hours, minutes) }));
      return;
    }

    if (name === "location") {
      setForm((s) => ({ ...s, location: value, place_id: undefined, lat: undefined, lng: undefined }));
      return;
    }

    setForm((s) => ({ ...s, [name]: value }));
  };

  /** ------- File input change (MULTIPLE) ------- */
  const handleFilesSelected = (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;

    setForm((s) => {
      const current = Array.isArray(s.media_files) ? [...s.media_files] : [];
      return { ...s, media_files: current };
    });

    // Respect MAX_MEDIA
    const room = Math.max(0, MAX_MEDIA - totalMediaCount);
    const toAdd = files.slice(0, room);

    // For videos/non-images: append directly. For images: crop queue.
    const images = [];
    const nonImages = [];
    toAdd.forEach((file) => {
      if (file.type?.startsWith("image/")) images.push(file);
      else nonImages.push(file);
    });

    // Append non-images immediately
    if (nonImages.length > 0) {
      setForm((s) => {
        const cur = Array.isArray(s.media_files) ? [...s.media_files] : [];
        const retainedCount = Array.isArray(s.existing_media_urls)
          ? s.existing_media_urls.length
          : 0;

        const room2 = Math.max(
          0,
          MAX_MEDIA - retainedCount - cur.length
        );
        if (room2 <= 0) return s;
        const slice = nonImages.slice(0, room2);
        return { ...s, media_files: cur.concat(slice) };
      });
    }

    // Queue images for cropping and start with the first
    if (images.length > 0) {
      setPendingCrops(images.map((file) => ({ file })));
      startCropForFile(images[0], null);
    }

    // clear the file input so re-selecting the same files later still triggers onChange
    e.target.value = "";
  };

  /** ------- Remove / Re-crop actions for a specific item ------- */
  const removeExistingMediaAt = (idx) => {
    setForm((s) => {
      const current = Array.isArray(s.existing_media_urls)
        ? [...s.existing_media_urls]
        : [];

      if (idx < 0 || idx >= current.length) return s;

      current.splice(idx, 1);

      return {
        ...s,
        existing_media_urls: current,
      };
    });
  };

  const removeMediaAt = (idx) => {
    setForm((s) => {
      const cur = Array.isArray(s.media_files) ? [...s.media_files] : [];
      if (idx < 0 || idx >= cur.length) return s;
      cur.splice(idx, 1);
      return { ...s, media_files: cur };
    });
  };

  const recropMediaAt = (idx) => {
    const f = mediaFiles[idx];
    if (!f || !f.type?.startsWith("image/")) return; // only images can be cropped
    startCropForFile(f, idx);
  };

  /** ------- Duration options ------- */
  const hourOptions = Array.from({ length: 13 }, (_, i) => i); // 0..12
  const minuteOptions = [0, 5, 10, 15, 20, 25, 30, 35, 40, 45, 50, 55];

  return (
    <>
      <div>
        <label className="mb-1 ml-3 block text-sm font-semibold text-[#6f665d]">
          Activity Title <span className="text-red-500">*</span>
        </label>

        <input
          type="text"
          name="title"
          placeholder="What did you do?"
          required
          className="w-full !min-h-0 !min-w-0 px-3 py-1.5 border rounded-2xl text-sm"
          value={form.title}
          onChange={handleChange}
        />
      </div>

      <div>
        <label className="mb-1 ml-3 block text-sm font-semibold text-[#6f665d]">
          Description
        </label>

        <MentionTextarea
          value={form.description}
          onChange={(val) =>
            handleChange({
              target: { name: "description", value: val },
            })
          }
          placeholder="Type @ to tag someone"
          className="w-full rounded-2xl border-neutral-200"
          textareaClassName="w-full"
          rows={4}
        />
      </div>

      <div>
        <label className="ml-3 block text-sm font-semibold text-[#6f665d]">
          Tags
        </label>

        <TagInput
          value={form.tags}
          onChange={(tags) =>
            setForm((current) => ({
              ...current,
              tags,
            }))
          }
        />

        <p className="ml-3 sm:mt-0.5 text-xs text-gray-500">
          Select an existing tag or press Enter to create one
        </p>
      </div>

      <div>
        <label className="mb-1 ml-3 block text-sm font-semibold text-[#6f665d]">
          Mood
        </label>

        <input
          type="text"
          name="mood"
          placeholder="How did you feel?"
          aria-label="Mood"
          className="w-full text-sm !min-h-0 !min-w-0 px-3 py-1.5 border rounded-2xl"
          value={form.mood}
          onChange={handleChange}
        />
      </div>

      {/* ==== Location (Google Places typeahead) ==== */}
      <div>
        <label className="mb-1 ml-3 block text-sm font-semibold text-[#6f665d]">
          Location
        </label>

        <LocationAutoComplete
          apiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY}
          value={form.location}
          onChange={(val) =>
            setForm((f) => ({
              ...f,
              location: val,
            }))
          }
          className="w-full text-sm"
          inputClassName="
            w-full rounded-3xl
            !min-h-0 !min-w-0 px-3 py-1.5
          "
          country="us"
        />
      </div>

      {/* ==== Date/Time + Duration ==== */}
      <div className="grid w-full min-w-0 max-w-full grid-cols-1 gap-3 md:grid-cols-2">
        {showTimestamp && (
          <div className="min-w-0 max-w-full">
            <label className="mb-1 ml-3 block text-sm font-semibold text-[#6f665d]">
              Date & Time
            </label>

            <input
              type="datetime-local"
              name="timestamp"
              className="
                block w-full min-w-0 max-w-full appearance-none
                rounded-3xl border bg-white
                !min-h-0 !min-w-0 px-3 py-1.5
                text-sm box-border
              "
              style={{
                width: "100%",
                minWidth: 0,
                maxWidth: "100%",
                WebkitAppearance: "none",
              }}
              value={form.timestamp}
              onChange={handleChange}
            />
          </div>
        )}

        <div className="min-w-0 max-w-full">
          <label className="mb-1 ml-3 block text-sm font-semibold text-[#6f665d]">
            Duration
          </label>

          <div className="flex w-full min-w-0 max-w-full gap-2">
            <label className="sr-only" htmlFor="dur_hours">
              Hours
            </label>

            <select
              id="dur_hours"
              className="
                w-1/2 min-w-0 rounded-3xl border bg-white
                !min-h-0 !min-w-0 px-3 py-1.5 text-sm
              "
              value={durHours}
              onChange={(e) => setDurHours(Number(e.target.value))}
            >
              {hourOptions.map((h) => (
                <option key={h} value={h}>
                  {h} {h === 1 ? "hour" : "hours"}
                </option>
              ))}
            </select>

            <label className="sr-only" htmlFor="dur_minutes">
              Minutes
            </label>

            <select
              id="dur_minutes"
              className="
                w-1/2 min-w-0 rounded-3xl border bg-white
                !min-h-0 !min-w-0 px-3 py-1.5 text-sm
              "
              value={durMinutes}
              onChange={(e) => setDurMinutes(Number(e.target.value))}
            >
              {minuteOptions.map((m) => (
                <option key={m} value={m}>
                  {m} {m === 1 ? "min" : "mins"}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {showCircles && !circleId && (
        <div>
          <label className="mb-1 ml-3 block text-sm font-semibold text-[#6f665d]">
            Select Circles
          </label>

          {/* Checkbox list (no modifier keys needed) */}
          <div className="max-h-56 overflow-auto rounded-2xl bg-white border p-2 space-y-1">
            {Array.isArray(joinedCircles) && joinedCircles.length > 0 ? (
              joinedCircles.map((c) => {
                const idNum = Number(c.id);
                const checked = Array.isArray(form.circle_ids)
                  ? form.circle_ids.includes(idNum)
                  : false;

                return (
                  <label
                    key={c.id}
                    className="flex cursor-pointer items-center gap-2 rounded-2xl px-2 py-1 hover:bg-blue-100"
                    onClick={(e) => {
                      e.preventDefault();

                      setForm((s) => {
                        const current = Array.isArray(s.circle_ids)
                          ? s.circle_ids.map(Number)
                          : [];

                        const exists = current.includes(idNum);

                        return {
                          ...s,
                          circle_ids: exists
                            ? current.filter((id) => id !== idNum)
                            : [...current, idNum],
                        };
                      });
                    }}
                  >
                    <input
                      type="checkbox"
                      className="sr-only"
                      checked={checked}
                      readOnly
                    />

                    <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-blue-600">
                      <Check
                        size={12}
                        weight="bold"
                        className={`text-white transition-opacity ${
                          checked ? "opacity-100" : "opacity-0"
                        }`}
                      />
                    </div>

                    <span className="text-sm">{c.name}</span>
                  </label>
                );
              })
            ) : (
              <div className="text-sm text-gray-500 px-2 py-1">
                You haven't joined any circles yet
              </div>
            )}
          </div>

          {Array.isArray(joinedCircles) && joinedCircles.length > 0 && (
            <p className="text-xs text-gray-500 ml-5 mt-1.5">
              You can select none, one, or many - click to toggle
            </p>
          )}
        </div>
      )}

      {/* ==== Media (MULTIPLE) ==== */}
      <div className="mt-2">
        <label className="mb-1 ml-3 block text-sm font-semibold text-[#6f665d]">
          Photos / Videos ({totalMediaCount}/{MAX_MEDIA})
        </label>

        <input
          type="file"
          name="media_files_input"
          accept="image/*,video/*"
          className="w-full rounded-2xl border px-3 py-2 text-sm"
          multiple
          disabled={totalMediaCount >= MAX_MEDIA}
          onChange={handleFilesSelected}
        />

        <p className="mt-1.5 ml-5 text-xs text-gray-500">
          On Mac hold ⌘ (Command) to multi-select
          <br />
          On iPhone/iPad tap “Select” in the picker to choose multiple
        </p>

        {(existingMediaUrls.length > 0 || previewUrls.length > 0) && (
          <div className="mt-3 grid grid-cols-2 gap-3 sm:grid-cols-3">
            {/* Existing saved media */}
            {existingMediaUrls.map((url, idx) => {
              const isExistingVideo =
                /\.(mp4|mov|m4v|webm|ogg)(\?.*)?$/i.test(url || "");

              return (
                <div
                  key={`existing-${url}-${idx}`}
                  className="relative overflow-hidden rounded border"
                >
                  {isExistingVideo ? (
                    <video
                      src={url}
                      controls
                      className="h-40 w-full object-cover"
                    />
                  ) : (
                    <img
                      src={url}
                      alt={`existing-media-${idx + 1}`}
                      className="h-40 w-full object-cover"
                    />
                  )}

                  <button
                    type="button"
                    onClick={() => removeExistingMediaAt(idx)}
                    className="absolute right-1 top-1 rounded-full border bg-white/90 !min-h-0 !min-w-0 px-2 py-0.5 text-xs text-red-600"
                  >
                    Remove
                  </button>
                </div>
              );
            })}

            {/* Newly selected local media */}
            {previewUrls.map(({ url, type }, idx) => (
              <div
                key={`new-${idx}`}
                className="relative overflow-hidden rounded border"
              >
                {type.startsWith("image/") ? (
                  <img
                    src={url}
                    alt={`new-media-${idx + 1}`}
                    className="h-40 w-full object-cover"
                  />
                ) : (
                  <video
                    src={url}
                    controls
                    className="h-40 w-full object-cover"
                  />
                )}

                <div className="absolute right-1 top-1 flex gap-1">
                  {type.startsWith("image/") && (
                    <button
                      type="button"
                      onClick={() => recropMediaAt(idx)}
                      className="rounded-full border bg-white/90 !min-h-0 !min-w-0 px-2 py-0.5 text-xs text-gray-800"
                    >
                      Crop
                    </button>
                  )}

                  <button
                    type="button"
                    onClick={() => removeMediaAt(idx)}
                    className="rounded-full border bg-white/90 !min-h-0 !min-w-0 px-2 py-0.5 text-xs text-red-600"
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {totalMediaCount >= MAX_MEDIA && (
          <p className="mt-1 text-xs text-gray-500">
            Remove an item before adding another
          </p>
        )}
      </div>

      {/* ==== Activity-level privacy (only you can see) ==== */}
      <label className="relative mt-3 ml-2 flex cursor-pointer items-start gap-2 text-sm text-gray-700">
        <input
          type="checkbox"
          className="peer absolute left-0 top-0 h-4 w-4 opacity-0"
          checked={form.visibility === "private"}
          onChange={(e) =>
            setForm((s) => ({
              ...s,
              visibility: e.target.checked ? "private" : "",
            }))
          }
        />

        <div className="mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center rounded-3xl bg-blue-600">
          <Check
            size={12}
            weight="bold"
            className={`text-white transition-opacity ${
              form.visibility === "private"
                ? "opacity-100"
                : "opacity-0"
            }`}
          />
        </div>

        <span className="leading-tight font-medium">
          Make this activity private

          <span className="block text-xs text-gray-500">
            Only you will see this in your feed - it won't appear to followers,
            circles, or search
          </span>
        </span>
      </label>

      <MediaCropperModal
        open={showCropper}
        image={cropSrc}
        crop={crop}
        zoom={zoom}
        aspect={aspect}
        cropPixels={cropPixels}
        onCropChange={setCrop}
        onZoomChange={setZoom}
        onCropComplete={onCropComplete}
        onAspectChange={setAspect}
        onCancel={cancelCrop}
        onApply={applyCropped}
      />
    </>
  );
}
